/**
 * Logging utility for frontend
 * Provides structured logging with different levels
 * In production, can be extended to send logs to external service
 */

type LogLevel = 'error' | 'warn' | 'info' | 'debug';

interface LogContext {
  [key: string]: unknown;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private productionEndpoint = (import.meta.env as unknown as { VITE_LOGGING_ENDPOINT?: string }).VITE_LOGGING_ENDPOINT;

  private log(level: LogLevel, message: string, context?: LogContext | unknown): void {
    if (!this.isDevelopment && level === 'debug') {
      return; // Skip debug logs in production
    }

    const timestamp = new Date().toISOString();

    // In development, use console
    if (this.isDevelopment) {
      switch (level) {
        case 'error':
          console.error(`[${timestamp}] ERROR:`, message, context);
          break;
        case 'warn':
          console.warn(`[${timestamp}] WARN:`, message, context);
          break;
        case 'info':
          console.info(`[${timestamp}] INFO:`, message, context);
          break;
        case 'debug':
          console.debug(`[${timestamp}] DEBUG:`, message, context);
          break;
      }
    }

    // In production, send to logging service (e.g., Sentry, DataDog, CloudWatch)
    if (!this.isDevelopment && (level === 'error' || level === 'warn')) {
      if (!this.productionEndpoint) {
        return;
      }

      const payload = {
        timestamp,
        level,
        message,
        context,
        url: window.location.href,
        userAgent: navigator.userAgent,
      };

      // Best-effort: don't block UX, don't throw.
      fetch(this.productionEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => undefined);
    }
  }

  error(message: string, context?: LogContext | unknown): void {
    this.log('error', message, context);
  }

  warn(message: string, context?: LogContext | unknown): void {
    this.log('warn', message, context);
  }

  info(message: string, context?: LogContext | unknown): void {
    this.log('info', message, context);
  }

  debug(message: string, context?: LogContext | unknown): void {
    this.log('debug', message, context);
  }
}

export const logger = new Logger();
